// Tap-pattern segreto per uscire dalla modalità kiosk (Sprint 7).
// 5 tap nell'angolo top-left in 3 secondi → modal con input PIN.

import { useEffect, useRef, useState } from 'react';
import { verifyKioskPin } from '../lib/api';

const TAPS_REQUIRED = 5;
const TAP_WINDOW_MS = 3000;

export function KioskExitButton() {
  const [tapsTimes, setTapsTimes] = useState<number[]>([]);
  const [showPin, setShowPin] = useState(false);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function handleTap() {
    const now = Date.now();
    setTapsTimes((prev) => {
      const recent = [...prev, now].filter((t) => now - t <= TAP_WINDOW_MS);
      if (recent.length >= TAPS_REQUIRED) {
        setShowPin(true);
        setTimeout(() => inputRef.current?.focus(), 50);
        return [];
      }
      return recent;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(pin)) {
      setError('PIN deve essere 4-8 cifre');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ok = await verifyKioskPin(pin);
      if (ok) {
        // Esce dal kiosk: rimuove override e va all'admin
        window.location.href = '/admin';
      } else {
        // Silenzio: nessun feedback su pin sbagliato (per non rivelare il pattern)
        setPin('');
        setShowPin(false);
      }
    } catch {
      setShowPin(false);
    } finally {
      setSubmitting(false);
    }
  }

  // Cleanup tap times che scadono
  useEffect(() => {
    if (tapsTimes.length === 0) return;
    const timer = setTimeout(() => {
      setTapsTimes((prev) => prev.filter((t) => Date.now() - t <= TAP_WINDOW_MS));
    }, TAP_WINDOW_MS + 100);
    return () => clearTimeout(timer);
  }, [tapsTimes]);

  return (
    <>
      {/* Hot zone invisibile in alto a sinistra */}
      <div
        aria-hidden
        onClick={handleTap}
        style={{
          position: 'fixed',
          top: 0,
          insetInlineStart: 0,
          width: 80,
          height: 80,
          zIndex: 9999,
          cursor: 'default',
        }}
      />
      {showPin ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{
              background: 'var(--paper)',
              padding: 24,
              borderRadius: 12,
              minWidth: 280,
              border: '1px solid var(--line)',
            }}
          >
            <h3 className="serif text-2xl mb-3" style={{ color: 'var(--ink)' }}>
              Esci da Kiosk
            </h3>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="PIN"
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 18,
                background: 'var(--surface)',
                color: 'var(--ink)',
              }}
              autoComplete="off"
            />
            {error ? (
              <div className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>
                {error}
              </div>
            ) : null}
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowPin(false);
                  setPin('');
                  setError(null);
                }}
                className="rounded-md px-3 py-2 text-sm"
                style={{
                  background: 'transparent',
                  color: 'var(--mute)',
                }}
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-md px-3 py-2 text-sm"
                style={{
                  background: 'var(--primary)',
                  color: '#fff',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Verifica…' : 'Conferma'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}
